import { useState } from 'react'
import { useWebSocket } from '../../../hooks/useWebSocket'
import { useAuth } from '../../../hooks/useAuth'
import DashboardCard from './DashboardCard'
import {
  SignalIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  StarIcon,
} from '@heroicons/react/24/outline'

interface RealTimeResultsProps {
  eventId?: string
  maxResults?: number
  showConnectionStatus?: boolean
}

export default function RealTimeResults({
  eventId,
  maxResults = 20,
  showConnectionStatus = true,
}: RealTimeResultsProps) {
  useAuth()
  const [selectedEvent, setSelectedEvent] = useState(eventId)

  const {
    isConnected,
    isConnecting,
    error,
    drillResults,
    joinEvent,
    leaveEvent,
    clearError,
    reconnect,
  } = useWebSocket({
    eventId: selectedEvent,
    autoReconnect: true,
  })

  // Filter and limit results
  const displayResults = drillResults
    .filter(result => !selectedEvent || result.athlete?.id) // Basic filter
    .slice(0, maxResults)

  const _handleEventSelect = (newEventId: string) => {
    if (selectedEvent) {
      leaveEvent(selectedEvent)
    }
    setSelectedEvent(newEventId)
    if (newEventId) {
      joinEvent(newEventId)
    }
  }

  const getConnectionStatus = () => {
    if (isConnecting) {
      return { text: 'Connecting...', color: 'text-yellow-600', icon: ArrowPathIcon }
    }
    if (isConnected) {
      return { text: 'Connected', color: 'text-green-600', icon: SignalIcon }
    }
    if (error) {
      return { text: 'Error', color: 'text-red-600', icon: ExclamationTriangleIcon }
    }
    return { text: 'Disconnected', color: 'text-gray-600', icon: ExclamationTriangleIcon }
  }

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const formatValue = (value: number, unit: string) => {
    if (unit === 'seconds') {
      return `${value.toFixed(2)}s`
    }
    if (unit === 'inches') {
      return `${value}"`
    }
    if (unit === 'pounds') {
      return `${value} lbs`
    }
    return `${value} ${unit}`
  }

  const getResultColor = (value: number, unit: string) => {
    // This is a simplified example - in production, you'd compare against percentiles
    if (unit === 'seconds') {
      // Lower is better for time-based drills
      if (value < 4.5) {
        return 'text-green-600 bg-green-50'
      }
      if (value < 5.0) {
        return 'text-blue-600 bg-blue-50'
      }
      return 'text-gray-600 bg-gray-50'
    } else {
      // Higher is better for distance/weight drills
      if (value > 35) {
        return 'text-green-600 bg-green-50'
      }
      if (value > 30) {
        return 'text-blue-600 bg-blue-50'
      }
      return 'text-gray-600 bg-gray-50'
    }
  }

  const status = getConnectionStatus()
  const StatusIcon = status.icon

  return (
    <DashboardCard
      title="Live Results"
      loading={isConnecting}
      error={error}
      actions={
        <div className="flex items-center space-x-3">
          {showConnectionStatus && (
            <div className="flex items-center space-x-1">
              <StatusIcon
                className={`h-4 w-4 ${status.color} ${isConnecting ? 'animate-spin' : ''}`}
              />
              <span className={`text-xs font-medium ${status.color}`}>{status.text}</span>
            </div>
          )}
          {error && (
            <button
              onClick={() => {
                clearError()
                reconnect()
              }}
              className="text-xs text-primary-600 hover:text-primary-800"
            >
              Retry
            </button>
          )}
        </div>
      }
    >
      {!isConnected && !isConnecting && !error && (
        <div className="text-center py-8">
          <SignalIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-sm text-gray-500">Connect to an event to see live results</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Connection Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={reconnect}
                  className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isConnected && displayResults.length === 0 && (
        <div className="text-center py-8">
          <div className="animate-pulse flex items-center justify-center">
            <SignalIcon className="h-8 w-8 text-green-500" />
          </div>
          <p className="text-sm text-gray-500 mt-2">Waiting for live results...</p>
        </div>
      )}

      {displayResults.length > 0 && (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {displayResults.map((result, index) => (
            <div
              key={`${result.id}-${index}`}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors animate-fade-in"
            >
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="text-sm font-medium text-gray-900">{result.athlete.name}</h4>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {result.drill.name}
                  </span>
                  {/* Show star for exceptional results */}
                  {result.value &&
                    ((result.unit === 'seconds' && result.value < 4.4) ||
                      (result.unit === 'inches' && result.value > 36)) && (
                      <StarIcon className="h-4 w-4 text-yellow-500" />
                    )}
                </div>
                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  <span>{result.drill.sport}</span>
                  <span>•</span>
                  <span>{formatTime(result.timestamp)}</span>
                </div>
              </div>
              <div className="text-right">
                <div
                  className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-semibold ${getResultColor(result.value, result.unit)}`}
                >
                  {formatValue(result.value, result.unit)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {isConnected && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{displayResults.length} recent results</span>
            <span className="flex items-center">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
              Live
            </span>
          </div>
        </div>
      )}
    </DashboardCard>
  )
}

// CSS for fade-in animation
const styles = `
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fade-in {
    animation: fade-in 0.3s ease-out;
  }
`

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style')
  styleSheet.textContent = styles
  document.head.appendChild(styleSheet)
}
